import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, CheckCircle, AlertTriangle, X, ExternalLink, Database, Youtube } from 'lucide-react';
import { BuildFromPdfResponse, ExtractedDrillInfo, DrillMatchSource, DrillAnimation, AnimationRequest, SaveTrainingSessionRequest } from '../Model';
import { restClient } from '../RestClient';
import { AuthService } from '../AuthService';
import { DrillAnimator } from '../Drills/DrillAnimator';

interface BuildFromPdfProps {
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
}

const MatchSourceIcon: React.FC<{ source: DrillMatchSource }> = ({ source }) => {
  switch (source) {
    case 'ExistingLibrary':
      return <Database size={16} className="text-success" />;
    case 'YouTubeSearch':
      return <Youtube size={16} className="text-info" />;
    case 'Unmatched':
      return <AlertTriangle size={16} className="text-warning" />;
  }
};

const MatchSourceBadge: React.FC<{ source: DrillMatchSource }> = ({ source }) => {
  const getClass = () => {
    switch (source) {
      case 'ExistingLibrary':
        return 'badge badge-success';
      case 'YouTubeSearch':
        return 'badge badge-info';
      case 'Unmatched':
        return 'badge badge-warning';
    }
  };

  const getLabel = () => {
    switch (source) {
      case 'ExistingLibrary':
        return 'Library Match';
      case 'YouTubeSearch':
        return 'YouTube';
      case 'Unmatched':
        return 'Unmatched';
    }
  };

  return (
    <span className={getClass()}>
      <MatchSourceIcon source={source} />
      <span className="ml-1">{getLabel()}</span>
    </span>
  );
};

interface DrillCardProps {
  drill: ExtractedDrillInfo;
  index: number;
}

const DrillCard: React.FC<DrillCardProps> = ({ drill, index }) => {
  const matchResult = drill.matchResult;
  const [animation, setAnimation] = useState<DrillAnimation | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);

  const handleAnimate = async () => {
    if (animation) {
      setShowAnimation(!showAnimation);
      return;
    }

    setIsAnimating(true);
    setShowAnimation(true);

    if (matchResult.isMatched && matchResult.matchedDrillId) {
      // Use existing drill-based endpoint for matched drills
      const result = await restClient.generateDrillAnimation(matchResult.matchedDrillId);
      if (result.IsSuccess) {
        setAnimation(result.Value);
      }
    } else {
      // Use description-based endpoint for unmatched drills
      const request: AnimationRequest = {
        name: drill.originalName,
        description: drill.parsedDescription,
        durationMinutes: drill.parsedDuration ?? 10,
        diagramBase64: drill.diagramBase64,
        diagramMediaType: drill.diagramMediaType,
      };
      const result = await restClient.generateAnimationFromDescription(request);
      if (result.IsSuccess) {
        setAnimation(result.Value);
      }
    }

    setIsAnimating(false);
  };

  return (
    <div className={`card mb-3 ${!matchResult.isMatched ? 'border-warning' : ''}`}>
      <div className="card-body">
        <div className="flex justify-between items-start mb-2">
          <div>
            <span className="text-muted text-sm">#{index + 1}</span>
            <h4 className="font-semibold mb-1">{drill.originalName}</h4>
            {drill.parsedDuration && (
              <span className="text-muted text-sm">{drill.parsedDuration} minutes</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleAnimate}
              disabled={isAnimating}
              className="btn btn-sm btn-primary"
            >
              {isAnimating ? 'Generating...' : showAnimation && animation ? 'Hide Animation' : '\u25B6 Animate'}
            </button>
            <MatchSourceBadge source={matchResult.source} />
          </div>
        </div>

        {drill.parsedDescription && (
          <p className="text-sm text-muted mb-2">{drill.parsedDescription}</p>
        )}

        {drill.diagramBase64 && (
          <div className="mt-2 mb-2">
            <img
              src={`data:${drill.diagramMediaType || 'image/png'};base64,${drill.diagramBase64}`}
              alt={`Pitch diagram for ${drill.originalName}`}
              style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 4 }}
            />
          </div>
        )}

        {matchResult.isMatched && matchResult.matchedDrillName && (
          <div className="mt-2 p-2 bg-light rounded">
            <div className="flex items-center gap-2">
              <CheckCircle size={16} className="text-success" />
              <span className="text-sm">
                Matched to: <strong>{matchResult.matchedDrillName}</strong>
                {matchResult.matchScore && (
                  <span className="text-muted ml-2">
                    ({Math.round(matchResult.matchScore * 100)}% match)
                  </span>
                )}
              </span>
            </div>
          </div>
        )}

        {!matchResult.isMatched && (
          <div className="mt-2 p-2 bg-warning-light rounded">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-warning" />
              <span className="text-sm text-warning-dark">
                {matchResult.unmatchedReason || 'No match found'}
              </span>
            </div>
          </div>
        )}

        {/* Animation */}
        {showAnimation && isAnimating && (
          <div className="mt-3 animation-loading">
            <div className="spinner" />
            <span className="text-muted">Generating animation...</span>
          </div>
        )}
        {showAnimation && animation && (
          <div className="mt-3">
            <DrillAnimator animation={animation} />
          </div>
        )}
      </div>
    </div>
  );
};

interface SummaryCardsProps {
  summary: BuildFromPdfResponse['summary'];
}

const SummaryCards: React.FC<SummaryCardsProps> = ({ summary }) => {
  return (
    <div className="grid grid-cols-4 gap-4 mb-6">
      <div className="card">
        <div className="card-body text-center">
          <div className="stat-value stat-value-lg">{summary.totalDrillsExtracted}</div>
          <div className="stat-label">Total Extracted</div>
        </div>
      </div>
      <div className="card">
        <div className="card-body text-center">
          <div className="stat-value stat-value-lg text-success">{summary.matchedFromLibrary}</div>
          <div className="stat-label">From Library</div>
        </div>
      </div>
      <div className="card">
        <div className="card-body text-center">
          <div className="stat-value stat-value-lg text-info">{summary.matchedFromYouTube}</div>
          <div className="stat-label">From YouTube</div>
        </div>
      </div>
      <div className="card">
        <div className="card-body text-center">
          <div className="stat-value stat-value-lg text-warning">{summary.unmatched}</div>
          <div className="stat-label">Unmatched</div>
        </div>
      </div>
    </div>
  );
};

export const BuildFromPdf: React.FC<BuildFromPdfProps> = ({ onError, onSuccess }) => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [result, setResult] = useState<BuildFromPdfResponse | null>(null);

  // Check admin access on mount
  useEffect(() => {
    if (!AuthService.isAdmin()) {
      onError?.('You must be an admin to access this feature.');
      navigate('/training-sessions');
    }
  }, [navigate, onError]);

  const handleFileSelect = async (selectedFile: File) => {
    // Basic MIME type check (can be inaccurate, but useful for quick feedback)
    if (selectedFile.type !== 'application/pdf') {
      onError?.('Please select a PDF file.');
      return;
    }

    // Validate file extension as an additional lightweight check
    if (!selectedFile.name.toLowerCase().endsWith('.pdf')) {
      onError?.('Please select a file with a .pdf extension.');
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      onError?.('File size must be less than 10MB.');
      return;
    }

    // Validate file content by checking for PDF magic bytes ("%PDF-")
    try {
      const buffer = await selectedFile.arrayBuffer();
      const headerBytes = new Uint8Array(buffer.slice(0, 5));
      const header = new TextDecoder('ascii').decode(headerBytes);
      if (!header.startsWith('%PDF-')) {
        onError?.('The selected file does not appear to be a valid PDF.');
        return;
      }
    } catch {
      onError?.('Unable to read the selected file. Please try again with a valid PDF.');
      return;
    }

    setFile(selectedFile);
    setResult(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleBuildSession = async () => {
    if (!file) {
      onError?.('Please select a PDF file first.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await restClient.buildSessionFromPdf(file);

      if (response.IsSuccess) {
        setResult(response.Value);
      } else {
        onError?.(response.Error?.description ?? 'Failed to process PDF.');
      }
    } catch (error) {
      onError?.('An unexpected error occurred while processing the PDF.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseSession = async () => {
    if (!result || isSaving) return;

    setIsSaving(true);

    try {
      const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      const name = `${result.sessionPlan.setup.sessionFocus} - ${today}`;

      const request: SaveTrainingSessionRequest = {
        name,
        sessionPlan: result.sessionPlan,
      };

      const saveResult = await restClient.saveTrainingSession(request);
      if (saveResult.IsSuccess) {
        onSuccess?.(`Session "${saveResult.Value.name}" saved successfully!`);
        navigate('/training-sessions');
      } else {
        onError?.(saveResult.Error.description ?? 'Failed to save session. Please try again.');
      }
    } catch (error) {
      onError?.('An unexpected error occurred while saving the session.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearFile = () => {
    setFile(null);
    setResult(null);
  };

  if (!AuthService.isAdmin()) {
    return null;
  }

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <h2 className="page-title">Build Session from PDF</h2>
        <p className="page-description">
          Upload a FAW Coach Ed PDF file to extract drills and create a training session automatically.
        </p>
      </div>

      {/* File Upload Area */}
      {!result && (
        <div className="card mb-6">
          <div className="card-body">
            <div
              className={`dropzone ${isDragging ? 'dropzone-active' : ''} ${file ? 'dropzone-has-file' : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              {file ? (
                <div className="flex items-center justify-center gap-4">
                  <FileText size={32} className="text-accent" />
                  <div>
                    <p className="font-semibold">{file.name}</p>
                    <p className="text-sm text-muted">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <button
                    onClick={handleClearFile}
                    className="btn btn-sm btn-secondary ml-4"
                    aria-label="Remove file"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <Upload size={48} className="mx-auto mb-3 text-muted" />
                  <p className="mb-2">Drag and drop a PDF file here, or</p>
                  <label htmlFor="pdf-upload" className="btn btn-primary">
                    Browse Files
                    <input
                      id="pdf-upload"
                      type="file"
                      accept=".pdf"
                      className="hidden"
                      onChange={(e) => {
                        const selectedFile = e.target.files?.[0];
                        if (selectedFile) {
                          handleFileSelect(selectedFile);
                        }
                      }}
                    />
                  </label>
                  <p className="text-sm text-muted mt-3">
                    Maximum file size: 10MB
                  </p>
                </div>
              )}
            </div>

            {file && !isLoading && (
              <div className="mt-4 text-center">
                <button
                  onClick={handleBuildSession}
                  className="btn btn-primary btn-lg"
                >
                  <FileText size={20} className="mr-2" />
                  Process PDF
                </button>
              </div>
            )}

            {isLoading && (
              <div className="mt-4 text-center">
                <div
                  className="loading-spinner mx-auto mb-2"
                  role="status"
                  aria-live="polite"
                  aria-label="Processing PDF, please wait"
                ></div>
                <p className="text-muted">Processing PDF and matching drills...</p>
                <p className="text-sm text-muted">This may take a moment if searching YouTube for drills.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Results */}
      {result && (
        <>
          {/* Summary */}
          <SummaryCards summary={result.summary} />

          {/* Warnings */}
          {result.summary.warnings.length > 0 && (
            <div className="alert alert-warning mb-4">
              <AlertTriangle size={20} className="mr-2" />
              <div>
                <strong>Warnings:</strong>
                <ul className="mt-1 ml-4 list-disc">
                  {result.summary.warnings.map((warning, idx) => (
                    <li key={idx} className="text-sm">{warning}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Session Plan Summary */}
          <div className="card mb-4">
            <div className="card-body">
              <h3 className="card-title mb-3">Generated Session Plan</h3>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <span className="text-muted text-sm">Focus</span>
                  <p className="font-semibold">{result.sessionPlan.setup.sessionFocus}</p>
                </div>
                <div>
                  <span className="text-muted text-sm">Duration</span>
                  <p className="font-semibold">{result.sessionPlan.setup.totalTime} minutes</p>
                </div>
                <div>
                  <span className="text-muted text-sm">Time Blocks</span>
                  <p className="font-semibold">{result.sessionPlan.timeBlocks.length}</p>
                </div>
                <div>
                  <span className="text-muted text-sm">Age Group</span>
                  <p className="font-semibold">{result.sessionPlan.setup.ageGroup || 'Not specified'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Extracted Drills */}
          <div className="card mb-4">
            <div className="card-body">
              <h3 className="card-title mb-4">Extracted Drills</h3>
              {result.extractedDrills.length > 0 ? (
                result.extractedDrills.map((drill, idx) => (
                  <DrillCard key={idx} drill={drill} index={idx} />
                ))
              ) : (
                <div className="text-center text-muted py-3">
                  <AlertTriangle className="inline-block mr-2 text-warning" size={18} />
                  <span>No drills were extracted from this PDF.</span>
                  <p className="mt-2 text-sm">
                    Check that the PDF contains clearly formatted drills (for example,
                    <code className="mx-1">Drill Name (10 mins)</code>) or try uploading a different file.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4 justify-center mb-6">
            <button
              onClick={handleUseSession}
              disabled={isSaving}
              className="btn btn-primary btn-lg"
            >
              <ExternalLink size={20} className="mr-2" />
              {isSaving ? 'Saving...' : 'Use This Session'}
            </button>
            <button
              onClick={handleClearFile}
              className="btn btn-secondary btn-lg"
            >
              Upload Different PDF
            </button>
          </div>
        </>
      )}

      {/* Help Text */}
      <div className="card">
        <div className="card-body">
          <h4 className="font-semibold mb-2">About PDF Processing</h4>
          <p className="text-muted text-sm mb-3">
            This feature parses FAW Coach Ed PDF files to extract training drills.
            For best results, ensure your PDF contains drills in the format:
          </p>
          <code className="block bg-light p-3 rounded text-sm mb-3">
            Drill Name (10 mins)
          </code>
          <p className="text-muted text-sm">
            Drills are matched against the existing drill library. If no match is found,
            a YouTube search is performed to find relevant training videos and a new drill
            is created automatically.
          </p>
        </div>
      </div>
    </div>
  );
};
